import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Search, ArrowUpRight } from 'lucide-react';
import SEO from '../components/SEO';
import PageLoader from '../components/PageLoader';
import api from '../utils/api';

const BLOCKS = [
  'All Blocks',
  'Fatehabad',
  'Ratia',
  'Tohana',
  'Bhuna',
  'Bhattu Kalan',
  'Jakhal',
  'Nagpur'
];

export default function VillagesPage() {
  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBlock, setSelectedBlock] = useState('All Blocks');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchVillages = async () => {
      try {
        setLoading(true);
        const res = await api.get('/villages');
        setVillages(res.data || []);
      } catch (err) {
        console.error('Error fetching villages:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchVillages();
  }, []);

  const filteredVillages = useMemo(() => {
    return villages.filter((v) => {
      const matchBlock =
        selectedBlock === 'All Blocks' ||
        v.block?.toLowerCase() === selectedBlock.toLowerCase();
      const q = searchQuery.trim().toLowerCase();
      const matchQuery =
        !q ||
        v.name?.toLowerCase().includes(q) ||
        v.block?.toLowerCase().includes(q) ||
        v.pincode?.includes(q);
      return matchBlock && matchQuery;
    });
  }, [villages, selectedBlock, searchQuery]);

  // Block counts map
  const blockCounts = useMemo(() => {
    const map = { 'All Blocks': villages.length };
    villages.forEach((v) => {
      if (v.block) {
        map[v.block] = (map[v.block] || 0) + 1;
      }
    });
    return map;
  }, [villages]);

  return (
    <>
      <SEO
        title="Fatehabad District Villages Directory | All Gram Panchayats & Blocks"
        description="Explore all 274+ villages and Gram Panchayats across Fatehabad, Ratia, Tohana, Bhuna, Bhattu Kalan, Jakhal and Nagpur blocks with local shops and services."
        canonical="/villages"
      />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-16">
        {/* Hero Section - Sleek Deep Dark Theme */}
        <div className="relative bg-gray-950 text-white py-12 md:py-16 px-4 sm:px-6 lg:px-8 border-b border-gray-800/80 shadow-2xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-gray-950 to-gray-950 pointer-events-none" />
          <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:20px_20px] pointer-events-none" />
          <div className="absolute -top-24 right-1/4 w-96 h-96 bg-primary-600/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-6xl mx-auto relative z-10 text-center">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gray-900/90 border border-gray-800 text-xs font-semibold mb-4 tracking-wide uppercase text-gray-300">
              <MapPin className="w-3.5 h-3.5 text-emerald-400" />
              Fatehabad District Rural Directory
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white mb-4">
              Villages of Fatehabad District
            </h1>
            <p className="text-gray-300 text-sm sm:text-base md:text-lg max-w-2xl mx-auto mb-8 font-normal leading-relaxed">
              Find verified local businesses, shops, CSC centers, schools, and essential services across all 7 development blocks and 274+ villages.
            </p>

            {/* Live Search Bar */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search any village name or pincode (e.g. Bighar, Badopal, 125050)..."
                className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-2xl shadow-xl shadow-black/20 focus:outline-none focus:ring-2 focus:ring-primary-400 text-sm sm:text-base border border-white/20"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-lg hover:bg-gray-200"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content Container */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          {/* Block Filter Badges */}
          <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 no-scrollbar scroll-smooth">
            {BLOCKS.map((block) => {
              const active = selectedBlock === block;
              const count = blockCounts[block] || 0;
              return (
                <button
                  key={block}
                  onClick={() => setSelectedBlock(block)}
                  className={`flex-shrink-0 px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all active:scale-95 flex items-center gap-1.5 ${
                    active
                      ? 'bg-primary-600 text-white shadow-md shadow-primary-600/30 ring-2 ring-primary-500/50'
                      : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <span>{block}</span>
                  <span
                    className={`text-[11px] px-1.5 py-0.5 rounded-full ${
                      active ? 'bg-white/25 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Results Summary */}
          <div className="flex justify-between items-center mb-6">
            <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
              Showing <span className="font-bold text-gray-900 dark:text-white">{filteredVillages.length}</span> villages
              {selectedBlock !== 'All Blocks' && <span> in <strong className="text-primary-600">{selectedBlock} Block</strong></span>}
            </div>
            <Link
              to="/submit-business"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-primary-600 dark:text-primary-400 hover:underline"
            >
              + Add a Village Shop
            </Link>
          </div>

          {/* Villages Grid */}
          {loading ? (
            <PageLoader message="Loading Fatehabad villages..." />
          ) : filteredVillages.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
              {filteredVillages.map((village) => (
                <Link
                  key={village._id}
                  to={`/village/${village.slug}`}
                  className="group bg-white dark:bg-gray-900 rounded-2xl p-4 border border-gray-200/80 dark:border-gray-800/80 hover:border-primary-500/50 hover:shadow-lg hover:shadow-primary-500/5 transition-all duration-200 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-start justify-between gap-1 mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-400 border border-primary-100 dark:border-primary-900/50">
                        {village.block}
                      </span>
                      <ArrowUpRight className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white text-sm sm:text-base group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors line-clamp-1 mb-1">
                      {village.name}
                    </h3>
                  </div>

                  <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-gray-400" />
                      {village.pincode}
                    </span>
                    <span className="text-[11px] font-medium text-primary-600 dark:text-primary-400 group-hover:underline">
                      View
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-900 rounded-3xl p-12 text-center border border-gray-200 dark:border-gray-800">
              <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3 opacity-50" />
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">No village found</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto mb-4">
                We couldn't find any village matching "{searchQuery}".
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedBlock('All Blocks');
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700"
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
